"use client";

import { useState, useEffect } from "react";
import { RenderingCanvas } from "@/components/legacy-rendering/RenderingCanvas";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RefreshCw } from "lucide-react";
import { CollapsibleSection } from "@/components/ui/collapsible-section";

export default function JewelryRenderingPage() {
  const [mainMode, setMainMode] = useState<
    "raymarching" | "pbr" | "raytracing"
  >("pbr");
  const [comparisonMode, setComparisonMode] = useState<
    "raymarching" | "pbr" | "raytracing"
  >("raytracing");
  const [resetTrigger, setResetTrigger] = useState(0);

  // ========================= 环境光控制状态 =========================
  const [useEnvironmentMap, setUseEnvironmentMap] = useState(true); // HDR环境贴图开关
  const [selectedHDR, setSelectedHDR] = useState("brown_photostudio_02_2k.hdr"); // 当前HDR文件选择
  const [environmentIntensity, setEnvironmentIntensity] = useState(1.0); // 环境光强度倍数
  const [environmentExposure, setEnvironmentExposure] = useState(2.0); // HDR曝光EV值

  // ========================= UI折叠控制状态 =========================
  // 渐进式信息披露：默认折叠高级设置，提供简洁的初始界面
  const [isEnvironmentCollapsed, setIsEnvironmentCollapsed] = useState(true); // 环境光设置折叠状态
  const [isMaterialCollapsed, setIsMaterialCollapsed] = useState(true); // 材质调节折叠状态

  // ========================= PBR材质参数状态 =========================
  // 仅PBR模式使用，8个完整的物理材质参数支持UI实时调节
  const [crystalMaterial, setCrystalMaterial] = useState({
    metalness: 0.05, // 金属度 [0,1]：0=绝缘体，1=金属，影响菲涅尔反射
    roughness: 0.08, // 微表面粗糙度 [0,1]：0=镜面，1=完全漫反射
    clearcoat: 0.8, // 透明涂层强度 [0,1]：模拟表面清漆层的反射
    clearcoatRoughness: 0.05, // 涂层粗糙度 [0,1]：控制清漆层的镜面程度
    transmission: 1.0, // 透射率 [0,1]：光线穿透材质的比例
    opacity: 1.0, // 不透明度 [0,1]：1-transparency，影响透明混合
    ior: 1.52, // 折射率 [1,2.5]：1.52=玻璃，1.33=水，影响折射角度
    dispersion: 0.1, // 色散系数 [0,5]：不同波长光的折射率差异，产生彩虹效果
  });

  // ========================= 视图控制事件处理 =========================

  /** 主视图与对比视图位置交换功能 */
  const handleSwap = () => {
    const temp = mainMode;
    setMainMode(comparisonMode);
    setComparisonMode(temp);
    setResetTrigger((prev) => prev + 1); // 触发相机重置
  };

  /** 主视图渲染技术选择，智能冲突处理 */
  const handleMainModeSelect = (mode: "raymarching" | "pbr" | "raytracing") => {
    setMainMode(mode);

    // 🎯 智能冲突解决：避免主视图与对比视图使用相同技术
    if (mode === comparisonMode) {
      const modes: Array<"raymarching" | "pbr" | "raytracing"> = [
        "raymarching",
        "raytracing",
        "pbr",
      ];
      const otherModes = modes.filter((m) => m !== mode);
      setComparisonMode(otherModes[0]); // 自动选择第一个不同的技术
    }
    setResetTrigger((prev) => prev + 1);
  };

  /** 对比视图渲染技术选择 */
  const handleComparisonModeSelect = (
    mode: "raymarching" | "pbr" | "raytracing",
  ) => {
    setComparisonMode(mode);
    setResetTrigger((prev) => prev + 1); // 同步触发相机重置
  };

  return (
    <TooltipProvider>
      <div className="w-full h-screen bg-background relative">
        {/* 🎯 右侧专业控制面板 - glassmorphism设计，固定宽度避免布局跳动 */}
        <div className="absolute top-4 right-4 z-30 flex flex-col gap-3">
          <div className="w-72 bg-background/60 backdrop-blur-xl rounded-lg shadow-xl border border-border/30 max-h-[calc(100vh-2rem)] overflow-y-auto scroll-smooth [scrollbar-gutter:stable] [scrollbar-width:thin] [scrollbar-color:hsl(var(--border)/0.3)_transparent] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:absolute [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-border/30 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-content hover:[&::-webkit-scrollbar-thumb]:bg-border/50">
            <div className="flex flex-col gap-4 p-3">
              {/* 🎯 实时对比预览窗口 - 棋盘背景增强透明度显示效果 */}
              <div className="flex flex-col gap-2">
                <div className="text-xs font-medium text-foreground">
                  对比预览
                </div>
                <div className="relative w-full h-48 rounded-lg overflow-hidden shadow-lg">
                  <div
                    className="w-full h-full"
                    style={{
                      backgroundImage: `
                            linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
                            linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
                            linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
                            linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
                          `,
                      backgroundSize: "12px 12px",
                      backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0px",
                      backgroundColor: "white",
                    }}
                  >
                    <RenderingCanvas
                      mode={comparisonMode}
                      resetTrigger={resetTrigger}
                      useEnvironmentMap={useEnvironmentMap}
                      selectedHDR={selectedHDR}
                      environmentIntensity={environmentIntensity}
                      environmentExposure={environmentExposure}
                    />

                    {/* 对比模式标识 */}
                    <div className="absolute top-2 left-2">
                      <Badge
                        variant="secondary"
                        className="text-xs bg-background/60 backdrop-blur-xl border border-border/30"
                      >
                        {comparisonMode === "raymarching"
                          ? "Ray Marching"
                          : comparisonMode === "pbr"
                            ? "PBR Rendering"
                            : "Ray Tracing"}
                      </Badge>
                    </div>

                    {/* 交换按钮 */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="secondary"
                          size="icon"
                          onClick={handleSwap}
                          className="absolute bottom-2 left-2 cursor-pointer bg-background/60 backdrop-blur-xl border border-border/30"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <div className="space-y-2">
                          <p className="font-medium">点击交换两个视图的位置</p>
                          <div className="text-xs space-y-1">
                            <div className="flex justify-between">
                              主视图:{" "}
                              <span className="font-medium">
                                {mainMode === "raymarching"
                                  ? "Ray Marching"
                                  : mainMode === "pbr"
                                    ? "PBR Rendering"
                                    : "Ray Tracing"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              对比视图:{" "}
                              <span className="font-medium">
                                {comparisonMode === "raymarching"
                                  ? "Ray Marching"
                                  : comparisonMode === "pbr"
                                    ? "PBR Rendering"
                                    : "Ray Tracing"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>

              {/* 柔和分割 */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-border/20 to-transparent h-px" />
              </div>

              {/* 分步式技术选择 */}
              <div className="flex flex-col gap-4">
                {/* 步骤1：选择主视图 */}
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-medium text-foreground">
                    选择主视图
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {(
                      [
                        {
                          mode: "raymarching" as const,
                          label: "Ray Marching",
                          short: "RM",
                        },
                        {
                          mode: "raytracing" as const,
                          label: "Ray Tracing",
                          short: "RT",
                        },
                        {
                          mode: "pbr" as const,
                          label: "PBR Rendering",
                          short: "PBR",
                        },
                      ] as const
                    ).map(({ mode, label, short }) => (
                      <Button
                        key={`main-${mode}`}
                        variant={mainMode === mode ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleMainModeSelect(mode)}
                        className={`h-8 text-xs cursor-pointer justify-center backdrop-blur-sm border-border/30 ${
                          mainMode === mode
                            ? "bg-foreground/80 hover:bg-foreground/90"
                            : "bg-background/20 hover:bg-background/30"
                        }`}
                      >
                        <span className="font-medium">{short}</span>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* 步骤2：选择对比技术 */}
                <div className="flex flex-col gap-2">
                  <div className="text-xs font-medium text-foreground">
                    选择对比技术
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {/* 对比技术选项 */}
                    {(
                      [
                        {
                          mode: "raymarching" as const,
                          label: "Ray Marching",
                          short: "RM",
                        },
                        {
                          mode: "raytracing" as const,
                          label: "Ray Tracing",
                          short: "RT",
                        },
                        {
                          mode: "pbr" as const,
                          label: "PBR Rendering",
                          short: "PBR",
                        },
                      ] as const
                    )
                      .filter(({ mode }) => mode !== mainMode) // 过滤掉与主视图相同的选项
                      .map(({ mode, label, short }) => (
                        <Button
                          key={`comp-${mode}`}
                          variant={
                            comparisonMode === mode ? "default" : "outline"
                          }
                          size="sm"
                          onClick={() => handleComparisonModeSelect(mode)}
                          className={`h-8 text-xs cursor-pointer justify-center backdrop-blur-sm border-border/30 ${
                            comparisonMode === mode
                              ? "bg-foreground/80 hover:bg-foreground/90"
                              : "bg-background/20 hover:bg-background/30"
                          }`}
                        >
                          <span className="font-medium">{short}</span>
                        </Button>
                      ))}
                  </div>
                </div>

                {/* 柔和分割 */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-border/20 to-transparent h-px" />
                </div>

                {/* 🎯 环境光设置 - 渐进式信息披露，HDR环境映射完整控制 */}
                <CollapsibleSection
                  title="环境光设置"
                  isCollapsed={isEnvironmentCollapsed}
                  onToggle={() =>
                    setIsEnvironmentCollapsed(!isEnvironmentCollapsed)
                  }
                >
                  {/* 环境贴图开关 */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-foreground">环境贴图</div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Checkbox
                          checked={useEnvironmentMap}
                          onCheckedChange={(checked) =>
                            setUseEnvironmentMap(checked === true)
                          }
                          className="cursor-pointer bg-background"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="text-xs">
                          启用环境贴图获得更真实的光照反射
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  {/* HDR 文件选择 */}
                  {useEnvironmentMap && (
                    <div className="flex flex-col gap-2">
                      <div className="text-xs text-foreground">HDR 场景</div>
                      <Select
                        value={selectedHDR}
                        onValueChange={setSelectedHDR}
                      >
                        <SelectTrigger className="w-full h-8 cursor-pointer text-xs bg-background/20 backdrop-blur-sm border-border/30">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            className="cursor-pointer"
                            value="brown_photostudio_02_2k.hdr"
                          >
                            棕色摄影工作室
                          </SelectItem>
                          <SelectItem
                            className="cursor-pointer"
                            value="studio_small_09_2k.hdr"
                          >
                            小型工作室
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* 环境光强度滑块 - 仅在启用环境贴图时显示 */}
                  {useEnvironmentMap && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-foreground">
                          环境光强度
                        </div>
                        <div className="text-xs font-mono text-foreground">
                          {environmentIntensity.toFixed(1)}
                        </div>
                      </div>
                      <Slider
                        value={[environmentIntensity]}
                        onValueChange={(value) =>
                          setEnvironmentIntensity(value[0])
                        }
                        min={0.1}
                        max={3.0}
                        step={0.1}
                        className="cursor-pointer"
                      />
                    </div>
                  )}

                  {/* HDR曝光控制滑块 - 仅在启用环境贴图时显示 */}
                  {useEnvironmentMap && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-foreground">HDR曝光</div>
                        <div className="text-xs font-mono text-foreground">
                          {environmentExposure.toFixed(1)}
                        </div>
                      </div>
                      <Slider
                        value={[environmentExposure]}
                        onValueChange={(value) =>
                          setEnvironmentExposure(value[0])
                        }
                        min={0.5}
                        max={10.0}
                        step={0.5}
                        className="cursor-pointer"
                      />
                    </div>
                  )}
                </CollapsibleSection>

                {/* 🎯 PBR水晶材质调节 - 上下文相关显示，8个物理参数实时控制 */}
                {mainMode === "pbr" && (
                  <>
                    {/* 分割线 */}
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-border/20 to-transparent h-px" />
                    </div>

                    <CollapsibleSection
                      title="水晶材质调节"
                      isCollapsed={isMaterialCollapsed}
                      onToggle={() =>
                        setIsMaterialCollapsed(!isMaterialCollapsed)
                      }
                    >
                      {/* 🎯 MeshPhysicalMaterial完整参数集：支持从物理真实到极端艺术化效果 */}
                      {/* 金属度 */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-foreground">
                            金属度 (Metalness)
                          </div>
                          <div className="text-xs font-mono text-foreground">
                            {crystalMaterial.metalness.toFixed(2)}
                          </div>
                        </div>
                        <Slider
                          value={[crystalMaterial.metalness]}
                          onValueChange={(value) =>
                            setCrystalMaterial((prev) => ({
                              ...prev,
                              metalness: value[0],
                            }))
                          }
                          min={0.0}
                          max={1.0}
                          step={0.01}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* 粗糙度 */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-foreground">
                            粗糙度 (Roughness)
                          </div>
                          <div className="text-xs font-mono text-foreground">
                            {crystalMaterial.roughness.toFixed(2)}
                          </div>
                        </div>
                        <Slider
                          value={[crystalMaterial.roughness]}
                          onValueChange={(value) =>
                            setCrystalMaterial((prev) => ({
                              ...prev,
                              roughness: value[0],
                            }))
                          }
                          min={0.0}
                          max={1.0}
                          step={0.01}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* 清漆层 */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-foreground">
                            清漆层 (Clearcoat)
                          </div>
                          <div className="text-xs font-mono text-foreground">
                            {crystalMaterial.clearcoat.toFixed(2)}
                          </div>
                        </div>
                        <Slider
                          value={[crystalMaterial.clearcoat]}
                          onValueChange={(value) =>
                            setCrystalMaterial((prev) => ({
                              ...prev,
                              clearcoat: value[0],
                            }))
                          }
                          min={0.0}
                          max={1.0}
                          step={0.01}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* 清漆层粗糙度 */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-foreground">
                            清漆粗糙度
                          </div>
                          <div className="text-xs font-mono text-foreground">
                            {crystalMaterial.clearcoatRoughness.toFixed(2)}
                          </div>
                        </div>
                        <Slider
                          value={[crystalMaterial.clearcoatRoughness]}
                          onValueChange={(value) =>
                            setCrystalMaterial((prev) => ({
                              ...prev,
                              clearcoatRoughness: value[0],
                            }))
                          }
                          min={0.0}
                          max={1.0}
                          step={0.01}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* 透射率 */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-foreground">
                            透射率 (Transmission)
                          </div>
                          <div className="text-xs font-mono text-foreground">
                            {crystalMaterial.transmission.toFixed(2)}
                          </div>
                        </div>
                        <Slider
                          value={[crystalMaterial.transmission]}
                          onValueChange={(value) =>
                            setCrystalMaterial((prev) => ({
                              ...prev,
                              transmission: value[0],
                            }))
                          }
                          min={0.0}
                          max={1.0}
                          step={0.01}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* 不透明度 */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-foreground">
                            不透明度 (Opacity)
                          </div>
                          <div className="text-xs font-mono text-foreground">
                            {crystalMaterial.opacity.toFixed(2)}
                          </div>
                        </div>
                        <Slider
                          value={[crystalMaterial.opacity]}
                          onValueChange={(value) =>
                            setCrystalMaterial((prev) => ({
                              ...prev,
                              opacity: value[0],
                            }))
                          }
                          min={0.0}
                          max={1.0}
                          step={0.01}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* 折射率 */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-foreground">
                            折射率 (IOR)
                          </div>
                          <div className="text-xs font-mono text-foreground">
                            {crystalMaterial.ior.toFixed(2)}
                          </div>
                        </div>
                        <Slider
                          value={[crystalMaterial.ior]}
                          onValueChange={(value) =>
                            setCrystalMaterial((prev) => ({
                              ...prev,
                              ior: value[0],
                            }))
                          }
                          min={1.0}
                          max={2.5}
                          step={0.01}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* 色散 */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-foreground">
                            色散 (Dispersion)
                          </div>
                          <div className="text-xs font-mono text-foreground">
                            {crystalMaterial.dispersion.toFixed(2)}
                          </div>
                        </div>
                        <Slider
                          value={[crystalMaterial.dispersion]}
                          onValueChange={(value) =>
                            setCrystalMaterial((prev) => ({
                              ...prev,
                              dispersion: value[0],
                            }))
                          }
                          min={0.0}
                          max={5.0}
                          step={0.01}
                          className="cursor-pointer"
                        />
                      </div>
                    </CollapsibleSection>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 🎯 全屏主视图区域 - 三种渲染技术的统一展示画布 */}
        <div className="w-full h-full">
          <RenderingCanvas
            mode={mainMode}
            resetTrigger={resetTrigger}
            showStats={true}
            useEnvironmentMap={useEnvironmentMap}
            selectedHDR={selectedHDR}
            environmentIntensity={environmentIntensity}
            environmentExposure={environmentExposure}
            crystalMaterial={crystalMaterial}
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
